import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as localStore from '../services/localPlantStore';

import { API_BASE_URL } from '../utils/apiBase';
import { saveOfflineSnapshot, loadOfflineSnapshot } from '../utils/offlineSnapshot';
const BACKEND_URL = API_BASE_URL;

export function usePlantData(isLocalMode, driveConnected, user = null) {
  const [plants, setPlants] = useState([]);
  const [filteredPlants, setFilteredPlants] = useState([]);
  const [incompletePlants, setIncompletePlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTerrariumOnly, setShowTerrariumOnly] = useState(() => localStorage.getItem('plant_terrarium_only') === 'true');
  const [showDormantOnly, setShowDormantOnly] = useState(() => localStorage.getItem('plant_dormant_only') === 'true');
  const [showNeedsDormancy, setShowNeedsDormancy] = useState(() => localStorage.getItem('plant_needs_dormancy') === 'true');
  // Family filter — set when the user taps the AI grouping suggestion
  // banner. Narrows the library to plants sharing the given botanical
  // family. Cleared via the "×" chip that renders above the grid.
  // Not persisted — it's an in-session focus, not a preference.
  const [familyFilter, setFamilyFilter] = useState(null);
  const [localPlantTotal, setLocalPlantTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  // In-flight flag for the manual "Refresh" button next to the
  // last-synced chip (Drive mode only). Disables the button + shows a
  // spinner while a forced re-fetch is running.
  const [refreshing, setRefreshing] = useState(false);
  // Last-synced timestamp for Drive-mode users. Set to Date.now() on
  // every successful `/api/plants` fetch; set to the mirror's
  // `saved_at` when we fall back to the offline snapshot so the chip
  // truthfully shows the snapshot's age. Persisted so the value
  // survives reloads / navigation.
  const [lastSyncedAt, setLastSyncedAt] = useState(() => {
    const raw = localStorage.getItem('plant_last_synced_at');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  // Migration state
  const [showMigration, setShowMigration] = useState(false);
  const [localPlantCount, setLocalPlantCount] = useState(0);

  // Load plants when drive connects (non-local mode)
  useEffect(() => {
    if (driveConnected && !isLocalMode) {
      loadPlants();
      checkForLocalPlants();
    }
  }, [driveConnected]);

  // Load local plants on mount (local mode)
  useEffect(() => {
    if (isLocalMode) {
      loadLocalPlants();
    }
  }, []);

  // Filter and sort whenever plants, searchQuery, terrariumOnly, or dormantOnly changes
  useEffect(() => {
    localStorage.setItem('plant_terrarium_only', showTerrariumOnly.toString());
    localStorage.setItem('plant_dormant_only', showDormantOnly.toString());
    localStorage.setItem('plant_needs_dormancy', showNeedsDormancy.toString());
    const active = plants.filter(p => !p.archived);
    const complete = active.filter(p => !p.incomplete);
    const incomplete = [...active.filter(p => p.incomplete)].sort((a, b) =>
      (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' })
    );
    let incompleteResult = incomplete;

    let result = [...complete];
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      // Match on common name, scientific name, or botanical family so
      // users can search their library by family (e.g. "Araceae") the
      // same way they search by plant name. Family lives in
      // care_details.family and is a common way experienced growers
      // organise their collection.
      const matchesQuery = (plant) => (
        plant.common_name?.toLowerCase().includes(query) ||
        plant.scientific_name?.toLowerCase().includes(query) ||
        plant.care_details?.family?.toLowerCase().includes(query)
      );
      result = result.filter(matchesQuery);
      incompleteResult = incomplete.filter(matchesQuery);
    }
    if (showTerrariumOnly) {
      const isTerrarium = (p) => {
        const env = (p.care_details?.environment || '').toLowerCase();
        return env.includes('terrarium') || env.includes('conservatory');
      };
      result = result.filter(isTerrarium);
      incompleteResult = incompleteResult.filter(isTerrarium);
    }
    if (showDormantOnly) {
      const currentMonth = new Date().getMonth() + 1; // 1-12
      const isDormantNow = (p) => {
        const months = p.care_details?.dormancy_months;
        return Array.isArray(months) && months.includes(currentMonth);
      };
      result = result.filter(isDormantNow);
      incompleteResult = incompleteResult.filter(isDormantNow);
    }
    if (showNeedsDormancy) {
      // Any plant that has a defined dormancy window at all — regardless
      // of whether we're currently inside that window. Lets users find the
      // subset of their library that requires seasonal care planning.
      const requiresDormancy = (p) => {
        const months = p.care_details?.dormancy_months;
        return Array.isArray(months) && months.length > 0;
      };
      result = result.filter(requiresDormancy);
      incompleteResult = incompleteResult.filter(requiresDormancy);
    }
    if (familyFilter) {
      // Focus mode — user tapped the AI family-grouping banner. Narrow
      // to plants sharing this family so they can review them together.
      const famLower = familyFilter.toLowerCase();
      const matchesFamily = (p) => (p.care_details?.family || '').toLowerCase() === famLower;
      result = result.filter(matchesFamily);
      incompleteResult = incompleteResult.filter(matchesFamily);
    }
    result.sort((a, b) => (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' }));
    setFilteredPlants(result);
    setIncompletePlants(incompleteResult);
  }, [searchQuery, plants, showTerrariumOnly, showDormantOnly, showNeedsDormancy, familyFilter]);

  async function checkForLocalPlants() {
    try {
      const count = await localStore.getPlantCount();
      if (count > 0) {
        setLocalPlantCount(count);
        setShowMigration(true);
      }
    } catch (err) {
      // IndexedDB not available or empty — local-mode migration prompt simply won't show
      console.debug('Local plant count check failed (non-fatal):', err?.message);
    }
  }

  async function loadPlants() {
    // Offline browse (iter 94, option A): try the network first, then
    // fall back to the IndexedDB mirror if the request fails so users
    // can at least browse a snapshot of their library. Writes are
    // blocked separately when `navigator.onLine` is false — see
    // `guardOnline` below.
    const sessionToken = localStorage.getItem('auth_hint');
    const userKey = user?.email || user?.id || 'anon';
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/plants`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      const sortedPlants = [...response.data].sort((a, b) =>
        (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' })
      );
      setPlants(sortedPlants);
      // Mirror the raw (unsorted) response so offline browsing sees the
      // exact same shape the server would have returned. Fire-and-forget.
      saveOfflineSnapshot(userKey, response.data);
      const now = Date.now();
      setLastSyncedAt(now);
      try { localStorage.setItem('plant_last_synced_at', String(now)); } catch { /* quota */ }
    } catch (err) {
      // Network / auth failure: try the offline mirror. If we have a
      // snapshot, surface a friendly toast so the user knows they're
      // browsing a cache. Only genuine network / offline errors qualify
      // — a 401 still returns null from the mirror so the auth flow
      // takes over normally.
      const isNetworkError = !navigator.onLine || err?.code === 'ERR_NETWORK' || err?.message === 'Network Error';
      if (isNetworkError) {
        const snap = await loadOfflineSnapshot(userKey);
        if (snap && snap.plants.length > 0) {
          const sortedPlants = [...snap.plants].sort((a, b) =>
            (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' })
          );
          setPlants(sortedPlants);
          if (snap.saved_at) {
            setLastSyncedAt(snap.saved_at);
            try { localStorage.setItem('plant_last_synced_at', String(snap.saved_at)); } catch { /* quota */ }
          }
          // Only announce the offline cache the first time this session —
          // repeated toasts on every silent refresh (e.g. after a queue
          // discard) are noisy and can cover other toasts like Undo.
          try {
            if (!sessionStorage.getItem('offline_cache_toast_shown')) {
              sessionStorage.setItem('offline_cache_toast_shown', '1');
              toast.info(`Loaded ${snap.plants.length} plants from your offline cache`);
            }
          } catch {
            toast.info(`Loaded ${snap.plants.length} plants from your offline cache`);
          }
          return;
        }
      }
      toast.error('Failed to load plants');
    } finally {
      setLoading(false);
    }
  }

  // Manual force-refresh for the button beside the last-synced chip.
  // Drive-mode only. Bypasses nothing special — it just re-runs the
  // Drive fetch, updates the mirror + last-synced timestamp, and gives
  // explicit success/failure feedback (loadPlants stays silent on
  // success). Offline taps short-circuit with a toast (handled by the
  // chip button, but guarded here too for safety).
  async function refreshPlants() {
    if (isLocalMode) return;
    if (!navigator.onLine) {
      toast.error('You\u2019re offline \u2014 reconnect to refresh.');
      return;
    }
    const sessionToken = localStorage.getItem('auth_hint');
    const userKey = user?.email || user?.id || 'anon';
    setRefreshing(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/plants`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      const sortedPlants = [...response.data].sort((a, b) =>
        (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' })
      );
      setPlants(sortedPlants);
      saveOfflineSnapshot(userKey, response.data);
      const now = Date.now();
      setLastSyncedAt(now);
      try { localStorage.setItem('plant_last_synced_at', String(now)); } catch { /* quota */ }
      toast.success('Library refreshed');
    } catch {
      toast.error('Could not refresh \u2014 please try again');
    } finally {
      setRefreshing(false);
    }
  }

  async function loadLocalPlants() {
    try {
      setLoading(true);
      const allPlants = await localStore.getAllPlants();
      const sorted = [...allPlants].sort((a, b) =>
        (a.common_name || '').localeCompare(b.common_name || '', undefined, { sensitivity: 'base' })
      );
      setPlants(sorted);
      setLocalPlantTotal(allPlants.length);
    } catch {
      toast.error('Failed to load plants from local storage');
    } finally {
      setLoading(false);
    }
  }

  function handlePlantAdded() {
    if (isLocalMode) loadLocalPlants();
    else loadPlants();
  }

  async function handlePlantDeleted(plantId) {
    try {
      let queued = false;
      if (isLocalMode) {
        await localStore.deletePlant(plantId);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.delete(`${BACKEND_URL}/api/plants/${plantId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        queued = !!resp?.data?.queued;
      }
      setPlants(prev => prev.filter(p => p.plant_id !== plantId));
      // When queued offline the interceptor already toasted "Saved
      // offline — will sync when you reconnect", so don't imply the
      // server already deleted it.
      if (!queued) toast.success('Plant deleted successfully');
      return true;
    } catch {
      toast.error('Failed to delete plant');
      return false;
    }
  }

  async function handleExportCSV() {
    if (exporting) return;
    try {
      setExporting(true);
      toast('Exporting CSV...');
      if (isLocalMode) {
        await localStore.exportLocalCSV();
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const response = await axios.get(`${BACKEND_URL}/api/plants/export/csv`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `plants_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success('Plants exported to CSV');
    } catch (error) {
      toast.error(error.message || 'Failed to export plants');
    } finally {
      setExporting(false);
    }
  }

  async function handleExportPDF() {
    if (exporting) return;
    try {
      setExporting(true);
      toast('Building PDF...');
      // Lazy-load jsPDF only when the user actually clicks — keeps the main
      // bundle lean since PDF export is a rarely-used feature.
      const { exportPlantsToPDF } = await import('../utils/pdfExport');
      // Export ALL non-archived plants (matches CSV behavior). Ignore
      // search filters + terrarium pill so the export is comprehensive.
      const active = plants.filter(p => !p.archived);
      if (active.length === 0) {
        toast.error('No plants to export');
        return;
      }
      exportPlantsToPDF(active);
      toast.success('Plants exported to PDF');
    } catch (error) {
      toast.error(error?.message || 'Failed to export plants to PDF');
    } finally {
      setExporting(false);
    }
  }

  async function handleShareLibrary() {
    if (exporting) return;
    try {
      setExporting(true);
      toast('Building shareable image…');
      const { shareMyLibrary } = await import('../utils/shareLibrary');
      const active = plants.filter((p) => !p.archived);
      if (active.length === 0) {
        toast.error('No plants to share');
        return;
      }
      const sessionToken = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_hint') : null;
      // Preload invite thumbnails so recipients see photos, not just names.
      let thumbnailMap = null;
      if (!isLocalMode && sessionToken) {
        try {
          const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
          thumbnailMap = await batchGetInviteThumbnails(
            active.filter((p) => !p.incomplete),
            { isLocalMode, sessionToken }
          );
        } catch (e) { console.debug('Thumbnail prep failed (share image):', e?.message); }
      }
      const result = await shareMyLibrary(active, {
        isLocalMode,
        senderName: user?.name || null,
        sessionToken,
        thumbnailMap,
      });
      if (result.shared) {
        toast.success(
          result.truncated
            ? `Shared! Link shows ${result.includedCount} of your ${result.activeCount} plants — a partial preview.`
            : 'Shared!'
        );
      } else if (result.downloaded) {
        toast.success(
          result.truncated
            ? `Image downloaded. Invite link shows ${result.includedCount} of your ${result.activeCount} plants.`
            : 'Image downloaded — post it anywhere'
        );
      }
    } catch (error) {
      toast.error(error?.message || 'Could not build shareable image');
    } finally {
      setExporting(false);
    }
  }

  async function handleCopyInvite(viewState = null) {
    if (exporting) return;
    try {
      setExporting(true);
      const { copyInviteLink } = await import('../utils/shareLibrary');
      const active = plants.filter((p) => !p.archived);
      if (active.length === 0) {
        toast.error('No plants to share');
        return;
      }
      const sessionToken = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_hint') : null;
      // Preload invite thumbnails so friends see photos on the Grow Along page.
      // Only relevant for the short-link path (Drive mode + signed in) —
      // fragment URLs can't carry image data without blowing past URL limits.
      const eligible = active.filter((p) => !p.incomplete);
      let thumbnailMap = null;
      const canEmbed = !isLocalMode && !!sessionToken;
      const progressToast = canEmbed && eligible.length > 6
        ? toast.loading(`Preparing invite… 0/${eligible.length}`, { id: 'invite-thumbs', position: 'bottom-center' })
        : null;
      if (canEmbed) {
        try {
          const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
          thumbnailMap = await batchGetInviteThumbnails(eligible, {
            isLocalMode,
            sessionToken,
            onProgress: (done, total) => {
              if (progressToast != null) {
                toast.loading(`Preparing invite… ${done}/${total}`, { id: 'invite-thumbs', position: 'bottom-center' });
              }
            },
          });
        } catch (e) { console.debug('Thumbnail prep failed (copy invite):', e?.message); }
      }
      if (progressToast != null) toast.dismiss('invite-thumbs');
      const result = await copyInviteLink(active, {
        senderName: user?.name || null,
        isLocalMode,
        sessionToken,
        thumbnailMap,
        viewState,
      });
      if (result.copied) {
        toast.success(
          result.truncated
            ? `Invite link copied — shows ${result.includedCount} of your ${result.activeCount} plants (partial preview).`
            : 'Invite link copied — paste it anywhere'
        );
      }
    } catch (error) {
      toast.error(error?.message || 'Could not copy invite link');
    } finally {
      setExporting(false);
    }
  }

  async function handlePreviewInvite(viewState = null) {
    try {
      const active = plants.filter((p) => !p.archived);
      if (active.length === 0) {
        toast.error('No plants to preview');
        return;
      }
      const sessionToken = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_hint') : null;
      const eligible = active.filter((p) => !p.incomplete);
      let thumbnailMap = null;
      const canEmbed = !isLocalMode && !!sessionToken;
      const progressToast = canEmbed && eligible.length > 6
        ? toast.loading(`Preparing invite… 0/${eligible.length}`, { id: 'invite-thumbs', position: 'bottom-center' })
        : null;
      if (canEmbed) {
        try {
          const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
          thumbnailMap = await batchGetInviteThumbnails(eligible, {
            isLocalMode,
            sessionToken,
            onProgress: (done, total) => {
              if (progressToast != null) {
                toast.loading(`Preparing invite… ${done}/${total}`, { id: 'invite-thumbs', position: 'bottom-center' });
              }
            },
          });
        } catch (e) { console.debug('Thumbnail prep failed (preview invite):', e?.message); }
      }
      if (progressToast != null) toast.dismiss('invite-thumbs');
      const { buildInviteUrl, createShortInviteUrl } = await import('../utils/inviteLink');
      const invite = isLocalMode
        ? buildInviteUrl(active, { senderName: user?.name || null, viewState })
        : await createShortInviteUrl(active, {
            senderName: user?.name || null,
            sessionToken,
            thumbnailMap,
            viewState,
          });
      // Open in a new tab so the user's own library page stays put.
      window.open(invite.url, '_blank', 'noopener,noreferrer');
      if (invite.truncated) {
        toast.info(`Preview shows ${invite.includedCount} of your ${invite.activeCount} plants.`);
      }
    } catch (error) {
      toast.error(error?.message || 'Could not open invite preview');
    }
  }

  function reloadAfterMigration() {
    loadPlants();
  }

  return {
    plants, filteredPlants, incompletePlants, loading,
    searchQuery, setSearchQuery,
    showTerrariumOnly, setShowTerrariumOnly,
    showDormantOnly, setShowDormantOnly,
    showNeedsDormancy, setShowNeedsDormancy,
    familyFilter, setFamilyFilter,
    lastSyncedAt,
    refreshing, refreshPlants,
    localPlantTotal, exporting,
    showMigration, setShowMigration, localPlantCount,
    handlePlantAdded, handlePlantDeleted, handleExportCSV, handleExportPDF, handleShareLibrary, handleCopyInvite, handlePreviewInvite,
    reloadAfterMigration,
  };
}
